import { useState } from 'react';
import moment from 'moment';
import { message } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';

export const useTreatmentPlan = (patientPlanData, handlePlanChange) => {
    const [selectedDateIndex, setSelectedDateIndex] = useState(0);
    const allDates = patientPlanData?.allDates || [];
    const dataSource = patientPlanData?.dataSource || [];

    // Convert time to 12-hour format with AM/PM
    const formatTime = (time) => {
        if (!time) return '';
        // Handle formats like "5.30 am", "5:30 am", "17:00", "5.30"
        const m = moment(time, ['h.mm a', 'h:mm a', 'HH:mm', 'H.mm', 'h.mm', 'h:mm']);
        return m.isValid() ? m.format('h:mm A') : time;
    };

    // Calculate duration between start and end time
    const calculateDuration = (startTime, endTime) => {
        if (!startTime || !endTime) return '';
        const start = moment(startTime, ['h.mm a', 'h:mm a', 'HH:mm', 'H.mm', 'h.mm', 'h:mm']);
        const end = moment(endTime, ['h.mm a', 'h:mm a', 'HH:mm', 'H.mm', 'h.mm', 'h:mm']);

        if (!start.isValid() || !end.isValid()) return '';

        const duration = moment.duration(end.diff(start));
        const hours = Math.floor(duration.asHours());
        const minutes = duration.minutes();

        if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h`;
        if (minutes > 0) return `${minutes}m`;
        return '';
    };

    // Get time period and color based on hour
    const getTimePeriod = (time) => {
        if (!time) return { period: 'Day', color: '#69b1ff', icon: '☀️' };

        const m = moment(time, ['h.mm a', 'h:mm a', 'HH:mm', 'H.mm', 'h.mm', 'h:mm']);
        const hour = m.isValid() ? m.hour() : 0;

        if (hour >= 5 && hour < 12) {
            return {
                period: 'Morning',
                color: '#ffa940',
                icon: '🌅',
                bgColor: '#fffbe6',
                borderColor: '#ffe7ba'
            };
        } else if (hour >= 12 && hour < 17) {
            return {
                period: 'Afternoon',
                color: '#69b1ff',
                icon: '☀️',
                bgColor: '#f0f5ff',
                borderColor: '#d6e4ff'
            };
        } else if (hour >= 17 && hour < 21) {
            return {
                period: 'Evening',
                color: '#b37feb',
                icon: '🌆',
                bgColor: '#faf5ff',
                borderColor: '#efdbff'
            };
        } else {
            return {
                period: 'Night',
                color: '#5cdbd3',
                icon: '🌙',
                bgColor: '#f0ffff',
                borderColor: '#d9f7f5'
            };
        }
    };

    // Calculate completion percentage for a date
    const getDateCompletion = (date) => {
        if (!dataSource.length) return 0;

        const totalServices = dataSource.length;
        const completedServices = dataSource.filter(service => {
            const value = service[date]?.medicineValue;
            return value && value !== 'Not Started' && value !== 'On Hold' && value !== 'Not Taken';
        }).length;

        return Math.round((completedServices / totalServices) * 100);
    };

    // Check if date is today or past
    const getDateStatus = (date) => {
        const isToday = moment(date).isSame(moment(), 'day');
        const isPast = moment(date).isBefore(moment(), 'day');
        return { isToday, isPast };
    };

    const handlePlanChangeWithFeedback = (recordIndex, date, newValue) => {
        handlePlanChange(recordIndex, date, newValue);

        const currentDateIndex = allDates.indexOf(date);
        const today = moment().startOf('day');
        const currentDate = moment(date, 'YYYY-MM-DD').startOf('day');

        if (!currentDate.isBefore(today) && currentDateIndex < allDates.length - 1 && newValue !== 'Not Taken') {
            const futureDatesCount = allDates.length - currentDateIndex - 1;
            message.success({
                content: (
                    <span>
                        <ThunderboltOutlined style={{ color: '#52c41a' }} />
                        {' '}Auto-filled {futureDatesCount} future date{futureDatesCount > 1 ? 's' : ''}
                    </span>
                ),
                duration: 2,
            });
        }
    };

    return {
        selectedDateIndex,
        setSelectedDateIndex,
        allDates,
        dataSource,
        formatTime,
        calculateDuration,
        getTimePeriod,
        getDateCompletion,
        getDateStatus,
        handlePlanChangeWithFeedback
    };
};
